import { db } from './index';
import { orders, users } from './schema';
import { eq, and } from 'drizzle-orm';

export async function confirmPaymentByPlanName(clerkId: string, planName: string) {
  try {
    const user = await db.query.users.findFirst({
      where: eq(users.clerkId, clerkId)
    });

    if (!user) {
      return { success: false, error: 'User not found' };
    }

    // Find all pending orders for this user
    const pendingOrders = await db.query.orders.findMany({
      where: and(
        eq(orders.userId, user.id),
        eq(orders.status, 'PENDING')
      ),
      with: {
        hosting: true,
        complement: true
      },
      orderBy: (orders, { desc }) => [desc(orders.createdAt)]
    });

    // Find the first order that matches the plan name
    const matchingOrder = pendingOrders.find(order => {
      if (order.hosting && (order.hosting.name === planName || order.hosting.displayName === planName)) return true;
      if (order.complement && (order.complement.name === planName || order.complement.displayName === planName)) return true;
      return false;
    });

    if (!matchingOrder) {
      return { success: false, error: 'No pending order found for this plan' };
    }

    // Update the order
    const updatedOrder = await db.update(orders)
      .set({ 
        status: 'PAID', 
        lastPaymentMethod: 'PAYPAL',
        updatedAt: new Date() 
      })
      .where(eq(orders.id, matchingOrder.id))
      .returning();

    // Send email notification
    try {
      const plan = matchingOrder.hosting || matchingOrder.complement;
      const planNameStr = plan?.displayName || plan?.name || 'Servicio';
      const planPrice = plan?.price?.toString() || '0';
      const planCurrency = plan?.currency || 'USD';
      const planPeriod = plan?.payTypeDisplay || (plan?.payType === 'ANUAL' ? 'anual' : 'mensual');

      await fetch('/api/send-status-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userEmail: user.email,
          userName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
          planName: planNameStr,
          planPrice: planPrice,
          planCurrency: planCurrency,
          planPeriod: planPeriod,
          status: 'PAID',
          type: 'STATUS_CHANGE'
        }),
      });
    } catch (emailError) {
      console.error('Error sending payment notification email:', emailError);
    }

    return { success: true, data: updatedOrder[0] };
  } catch (error) {
    console.error('Error confirming payment:', error);
    return { success: false, error: 'Failed to confirm payment' };
  }
}

export async function markOrderAsPaypalPending(clerkId: string, planName: string) {
  try {
    const user = await db.query.users.findFirst({
      where: eq(users.clerkId, clerkId)
    });

    if (!user) {
      return { success: false, error: 'User not found' };
    }

    // Find all pending orders for this user
    const pendingOrders = await db.query.orders.findMany({
      where: and(
        eq(orders.userId, user.id),
        eq(orders.status, 'PENDING')
      ),
      with: {
        hosting: true,
        complement: true
      },
      orderBy: (orders, { desc }) => [desc(orders.createdAt)]
    });

    // Find the first order that matches the plan name
    const matchingOrder = pendingOrders.find(order => {
      if (order.hosting && (order.hosting.name === planName || order.hosting.displayName === planName)) return true;
      if (order.complement && (order.complement.name === planName || order.complement.displayName === planName)) return true;
      return false;
    });

    if (!matchingOrder) {
      return { success: false, error: 'No pending order found for this plan' };
    }

    // Update the order to show PAYPAL as payment method but keep status as PENDING
    const updatedOrder = await db.update(orders)
      .set({ 
        lastPaymentMethod: 'PAYPAL',
        updatedAt: new Date() 
      })
      .where(eq(orders.id, matchingOrder.id))
      .returning();

    return { success: true, data: updatedOrder[0] };

  } catch (error) {
    console.error('Error marking order as pending paypal:', error);
    return { success: false, error: 'Failed to update order' };
  }
}

export async function listOrders() {
  try {
    const results = await db.query.orders.findMany({
      with: {
        user: true,
        hosting: true,
        complement: true
      },
      orderBy: (orders, { desc }) => [desc(orders.createdAt)]
    });
    return { success: true, data: results };
  } catch (error) {
    console.error('Error listing orders:', error);
    return { success: false, error: 'Failed to list orders' };
  }
}

export async function deleteOrder(id: string) {
  try {
    const result = await db.delete(orders).where(eq(orders.id, id)).returning();
    return { success: true, data: result[0] };
  } catch (error) {
    console.error('Error deleting order:', error);
    return { success: false, error: 'Failed to delete order' };
  }
}

export async function updateOrder(id: string, data: Partial<typeof orders.$inferInsert>) {
  try {
    const result = await db.update(orders).set(data).where(eq(orders.id, id)).returning();
    return { success: true, data: result[0] };
  } catch (error) {
    console.error('Error updating order:', error);
    return { success: false, error: 'Failed to update order' };
  }
}

export async function listUserOrders(clerkId: string) {
  try {
    const user = await db.query.users.findFirst({
      where: eq(users.clerkId, clerkId)
    });

    if (!user) {
      return { success: false, error: 'User not found' };
    }

    const results = await db.query.orders.findMany({
      where: eq(orders.userId, user.id),
      with: {
        hosting: true,
        complement: true
      },
      orderBy: (orders, { desc }) => [desc(orders.createdAt)]
    });
    return { success: true, data: results };
  } catch (error) {
    console.error('Error listing user orders:', error);
    return { success: false, error: 'Failed to list user orders' };
  }
}

export async function createOrder(clerkId: string, itemId: string, domain?: string, itemType: 'hosting' | 'complement' = 'hosting', quantity: number = 1) {
  try {
    const user = await db.query.users.findFirst({
      where: eq(users.clerkId, clerkId)
    });

    if (!user) {
      return { success: false, error: 'User not found in database. Please try logging in again.' };
    }

    const values: any = {
      userId: user.id,
      domain: domain || null,
      status: 'PENDING' as const,
      quantity: quantity
    };

    if (itemType === 'complement') {
      values.complementId = itemId;
    } else {
      values.hostingId = itemId;
    }

    const result = await db.insert(orders).values(values).returning();

    return { success: true, data: result[0] };
  } catch (error) {
    console.error('Error creating order:', error);
    return { success: false, error: 'Failed to create order' };
  }
}

export async function createOrderForUser(userId: string, hostingId?: string, domain?: string, status: any = 'PENDING', createdAt?: Date, complementId?: string, quantity: number = 1, lastPaymentMethod?: string) {
  try {
    const values: any = {
      userId: userId,
      domain: domain || null,
      status: status,
      createdAt: createdAt || new Date(),
      quantity: quantity,
      lastPaymentMethod: lastPaymentMethod || null
    };

    if (complementId) {
      values.complementId = complementId;
    } else if (hostingId) {
      values.hostingId = hostingId;
    }

    const result = await db.insert(orders).values(values).returning();

    return { success: true, data: result[0] };
  } catch (error) {
    console.error('Error creating order for user:', error);
    return { success: false, error: 'Failed to create order' };
  }
}
